/**
 * EntraUserMultiSelect — multi-select autosuggest/typeahead for picking one or more Microsoft
 * Entra ID (Azure AD) directory users. Built on react-select (already used elsewhere in this app
 * for multi-select pickers, e.g. the Vendor selector on the Receiving Shipment screen) with
 * isMulti, so type-to-filter search, multi-pick, and removable chips come for free.
 *
 * Data source: GET api/User/entra-users (UserService.getEntraUsers), fetched once and cached at
 * module scope for the life of the page — the directory does not change within a session, and
 * this avoids re-fetching it every time a form/modal using this control is opened.
 *
 * Pre-selection (edit mode): pass `value` as [{ id, displayName }]. Selected chips render
 * immediately from that snapshot even before the directory finishes loading, and are reconciled
 * against the live directory once it arrives (so a since-removed/renamed user still shows using
 * the name that was saved with the record, rather than disappearing).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { UserService } from '../../../service/api/user-service';

let directoryPromise = null;
function loadDirectoryOnce() {
  if (!directoryPromise) {
    directoryPromise = UserService.getEntraUsers().catch((err) => {
      directoryPromise = null; // allow a retry on the next mount after a failure
      throw err;
    });
  }
  return directoryPromise;
}

export default function EntraUserMultiSelect({
  value = [],
  onChange,
  placeholder = 'Search by name…',
  isDisabled = false,
  id,
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    setError('');
    loadDirectoryOnce()
      .then((list) => { if (alive.current) setUsers(list); })
      .catch(() => { if (alive.current) setError('Could not load the user directory.'); })
      .finally(() => { if (alive.current) setLoading(false); });
    return () => { alive.current = false; };
  }, []);

  const options = useMemo(
    () => users.map((u) => ({ value: u.id, label: u.displayName, user: u })),
    [users],
  );

  // Reconcile the saved {id, displayName} snapshot against the live directory so a name saved
  // earlier still shows even if that person is no longer in the directory or renamed since.
  const selectedOptions = useMemo(
    () => (value || []).map((v) => {
      const match = options.find((o) => o.value === v.id);
      return match || { value: v.id, label: v.displayName || v.id, user: v };
    }),
    [value, options],
  );

  const handleChange = (selected) => {
    const next = (selected || []).map((o) => ({
      id: o.value,
      displayName: o.label,
    }));
    onChange?.(next);
  };

  const printSummary = selectedOptions.length
    ? selectedOptions.map((o) => o.label).join(', ')
    : ' ';

  return (
    <div>
      {/* react-select's internal CSS-in-JS doesn't take print overrides reliably. Simplest robust
          fix: hide the interactive control on the printed page and show a plain text box with
          the same names instead — the same pattern as every other locked field on this form. */}
      <style>{`
        .eums-print-summary{display:none}
        @media print{
          .eums-interactive{display:none}
          .eums-print-summary{display:block;font:inherit;font-size:13px;color:inherit;
            border:1px solid #9AA5B4;border-radius:6px;padding:7px 9px;min-height:34px;
            white-space:pre-wrap;word-break:break-word;box-sizing:border-box}
        }
      `}</style>
      <div className="eums-interactive">
        <Select
          inputId={id}
          isMulti
          isDisabled={isDisabled}
          isLoading={loading}
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          placeholder={placeholder}
          classNamePrefix="react-select"
          noOptionsMessage={() => (loading ? 'Loading users…' : error || 'No users found')}
        />
        {error && <div className="text-danger" style={{ fontSize: 11.5, marginTop: 4 }}>{error}</div>}
      </div>
      <div className="eums-print-summary">{printSummary}</div>
    </div>
  );
}
